import { t } from '@i18n';

/**
 * Material field validation constraints
 */
export const MaterialValidation = {
    NAME_MIN_LENGTH: 3,
    NAME_MAX_LENGTH: 50,
};

/**
 * Material validation error messages.
 * Getters resolve at read time so the message follows the active language —
 * a plain object literal would freeze the copy at import time.
 */
export const MaterialValidationMessages = {
    get NAME_REQUIRED() { return t('contentModals:manageMaterial.validation.nameRequired'); },
    get NAME_TOO_SHORT() { return t('contentModals:manageMaterial.validation.nameTooShort'); },
    get NAME_TOO_LONG() { return t('contentModals:manageMaterial.validation.nameTooLong'); },
    get COMMUNITY_INVALID() { return t('contentModals:manageMaterial.validation.communityInvalid'); },
    get MATERIAL_REQUIRED() { return t('contentModals:manageMaterial.validation.materialRequired'); },
    get AVAILABILITY_REQUIRED() { return t('contentModals:manageMaterial.validation.availabilityRequired'); },
    get AVAILABILITY_PLANS_REQUIRED() { return t('contentModals:manageMaterial.validation.availabilityPlansRequired'); },
    get TYPE_INVALID() { return t('contentModals:manageMaterial.validation.typeInvalid'); },
    get ID_REQUIRED() { return t('contentModals:manageMaterial.validation.idRequired'); },
};

/**
 * Validate material name
 * @param {string} name - Material name to validate
 * @returns {{valid: boolean, error?: string}}
 */
export function validateMaterialName(name) {
    if (!name) {
        return {
            valid: false,
            error: MaterialValidationMessages.NAME_REQUIRED
        };
    }

    if (name.length < MaterialValidation.NAME_MIN_LENGTH) {
        return {
            valid: false,
            error: MaterialValidationMessages.NAME_TOO_SHORT
        };
    }

    if (name.length > MaterialValidation.NAME_MAX_LENGTH) {
        return {
            valid: false,
            error: MaterialValidationMessages.NAME_TOO_LONG
        };
    }

    return { valid: true };
}

/**
 * Normalize API or form availability to internal shape: undefined | null | number[]
 * - API may send { plans: [...] } or legacy array
 * - ["all"] or { plans: ["all"] } → null (public)
 * - { plans: null } → null (public)
 * @param {*} availability
 * @returns {undefined|null|number[]}
 */
export function normalizeAvailabilityForForm(availability) {
    if (availability === undefined) return undefined;
    if (availability === null) return null;

    if (Array.isArray(availability)) {
        if (availability.includes('all')) return null;
        return availability;
    }

    if (typeof availability === 'object' && availability !== null && 'plans' in availability) {
        if (availability.plans === null || availability.plans === undefined) {
            return null;
        }
        if (Array.isArray(availability.plans)) {
            if (availability.plans.includes('all')) return null;
            return availability.plans;
        }
    }

    return undefined;
}

/**
 * Serialize internal availability for API: { plans: null } (public) or { plans: [...] }
 * @param {null|number[]} availability
 * @returns {{ plans: null|number[] }}
 */
export function availabilityToPayload(availability) {
    if (availability === null) {
        return { plans: null };
    }
    return { plans: availability };
}

/**
 * Validate material availability
 * - null means "public/available to all" (valid)
 * - array must have at least one plan ID if specified
 * @param {Array|null} availability - Material availability
 * @returns {{valid: boolean, error?: string}}
 */
export function validateMaterialAvailability(availability) {
    // undefined is not allowed
    if (availability === undefined) {
        return {
            valid: false,
            error: MaterialValidationMessages.AVAILABILITY_REQUIRED
        };
    }

    // null means "public/all" - valid
    if (availability === null) {
        return { valid: true };
    }

    // If it's an array (specific plans mode), must have at least one plan
    if (Array.isArray(availability)) {
        if (availability.length === 0) {
            return {
                valid: false,
                error: MaterialValidationMessages.AVAILABILITY_PLANS_REQUIRED
            };
        }
        return { valid: true };
    }

    // Invalid type
    return {
        valid: false,
        error: MaterialValidationMessages.AVAILABILITY_REQUIRED
    };
}
